import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, ApiError, type ClientListEntry, type ClientStatus, type Company, type Me } from "../lib/api";
import { whenLast } from "../lib/dates";

const pad = (n: number) => String(n).padStart(2, "0");

/** One line of company facts: domain · location · industry. */
export function CompanyFacts({ company }: { company: Pick<Company, "websiteDomain" | "location" | "industry"> }) {
  const facts = [company.websiteDomain, company.location, company.industry].filter(Boolean);
  return facts.length ? <span className="secondary">{facts.join(" · ")}</span> : null;
}

export function StatusChip({ status }: { status: ClientStatus }) {
  return <span className={`status ${status === "lead" ? "pending" : ""}`}>{status}</span>;
}

/** Find a company in the shared directory, or add it; plus this agency's own list. */
export function Companies({ me }: { me: Me }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Company[] | null>(null);
  const [adding, setAdding] = useState(false);
  const search = useAction();
  const quick = useAction();

  // Debounced search; ignore responses for stale queries.
  useEffect(() => {
    const query = q.trim();
    setAdding(false);
    if (query.length < 2) {
      setResults(null);
      return;
    }
    let current = true;
    const timer = setTimeout(() => {
      void search.run(async () => {
        const { results } = await api.searchCompanies(query);
        if (current) setResults(results);
      });
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const addAsLead = (company: Company) =>
    void quick.run(async () => {
      await api.saveClient(company.id, { status: "lead" });
      navigate(`/companies/${company.id}`);
    });

  return (
    <>
      <div className="page-head">
        <p className="label">Directory</p>
        <h1 className="display">
          Every company, <em>one record</em>.
        </h1>
        <p className="lede">
          Company profiles are shared across agencies so nobody types Apple in twice. Your status, notes, contacts and terms
          stay private to {me.tenant.name}.
        </p>
      </div>

      <section className="section" aria-labelledby="find-h">
        <div className="section-head">
          <h2 id="find-h" className="label">
            Find or add a company
          </h2>
          <span className="button-row">
            <span className="label figures">{results ? `${pad(results.length)} found` : ""}</span>
            <Link className="link-btn" to="/import?type=companies">
              Import spreadsheet
            </Link>
          </span>
        </div>
        <div className="search">
          <label className="sr-only" htmlFor="company-search">
            Search by name or website
          </label>
          <input
            id="company-search"
            type="search"
            placeholder="Name or website, e.g. Monzo or monzo.com"
            autoComplete="off"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <ErrorNotice error={search.error ?? quick.error} />

        {results && (
          <ol className="index">
            {results.map((c, i) => (
              <li key={c.id}>
                <span className="num" aria-hidden>
                  {pad(i + 1)}
                </span>
                <span className="main">
                  <Link className="primary" to={`/companies/${c.id}`}>
                    {c.name}
                  </Link>
                  <CompanyFacts company={c} />
                </span>
                <span className="actions">
                  {c.client ? (
                    <StatusChip status={c.client.status} />
                  ) : (
                    <button type="button" className="link-btn" disabled={quick.pending} onClick={() => addAsLead(c)}>
                      Add as lead
                    </button>
                  )}
                </span>
              </li>
            ))}
            <li className="add-row">
              <span className="num" aria-hidden>
                +
              </span>
              <span className="main">
                <span className="secondary">
                  {results.length ? "Not the one you mean?" : `Nothing called “${q.trim()}” yet.`}
                </span>
              </span>
              <span className="actions">
                <button type="button" className="link-btn" onClick={() => setAdding(true)}>
                  Add a new company
                </button>
              </span>
            </li>
          </ol>
        )}

        {adding && <NewCompanyForm initialName={q.trim()} onCancel={() => setAdding(false)} />}
      </section>

      <YourList />
    </>
  );
}

function NewCompanyForm({ initialName, onCancel }: { initialName: string; onCancel: () => void }) {
  const navigate = useNavigate();
  const { pending, error, setError, run } = useAction();
  const [existing, setExisting] = useState<string | null>(null);
  const [name, setName] = useState(initialName);
  const [website, setWebsite] = useState(initialName.includes(".") && !initialName.includes(" ") ? initialName : "");
  const [location, setLocation] = useState("");
  const [industry, setIndustry] = useState("");
  const [status, setStatus] = useState<ClientStatus>("lead");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setExisting(null);
    void run(async () => {
      try {
        const { company } = await api.createCompany({
          name: name.trim(),
          website: website.trim() || null,
          location: location.trim() || null,
          industry: industry.trim() || null,
          status,
        });
        navigate(`/companies/${company.id}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && typeof err.details.existingCompanyId === "string") {
          setExisting(err.details.existingCompanyId);
          setError(err.message);
          return;
        }
        throw err;
      }
    });
  };

  return (
    <form className="form panel" onSubmit={submit} aria-labelledby="new-company-h">
      <h3 id="new-company-h" className="title">
        New <em>company</em>
      </h3>
      <div className="grid-2">
        <Field label="Company name" required maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
        <Field
          label="Website"
          inputMode="url"
          placeholder="example.com"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          hint="Used to spot duplicates."
        />
        <Field label="Location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. London" />
        <Field label="Industry" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="e.g. Fintech" />
      </div>
      <fieldset className="segmented">
        <legend className="label">On your list as</legend>
        {(["lead", "client"] as const).map((s) => (
          <label key={s}>
            <input type="radio" name="status" value={s} checked={status === s} onChange={() => setStatus(s)} />
            <span>{s === "lead" ? "Lead — still winning the business" : "Client — terms agreed"}</span>
          </label>
        ))}
      </fieldset>
      <ErrorNotice error={error} />
      {existing && (
        <p>
          <Link to={`/companies/${existing}`}>Open the existing record →</Link>
        </p>
      )}
      <div className="button-row">
        <button className="btn" disabled={pending}>
          {pending ? "Adding…" : "Add company"}
        </button>
        <button type="button" className="link-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function YourList() {
  const [filter, setFilter] = useState<ClientStatus | "all">("all");
  const [companies, setCompanies] = useState<ClientListEntry[] | null>(null);
  const load = useAction();

  useEffect(() => {
    void load.run(async () => setCompanies((await api.clients()).clients));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const count = (s: ClientStatus) => companies?.filter((c) => c.client?.status === s).length ?? 0;
  const shown = companies?.filter((c) => filter === "all" || c.client?.status === filter) ?? [];

  return (
    <section className="section" aria-labelledby="list-h">
      <div className="section-head">
        <h2 id="list-h" className="label">
          Your list
        </h2>
        <div className="tabs" role="group" aria-label="Filter your list">
          {(
            [
              ["all", `All ${pad(companies?.length ?? 0)}`],
              ["lead", `Leads ${pad(count("lead"))}`],
              ["client", `Clients ${pad(count("client"))}`],
            ] as const
          ).map(([value, text]) => (
            <button
              key={value}
              type="button"
              className="tab figures"
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {text}
            </button>
          ))}
        </div>
      </div>
      <ErrorNotice error={load.error} />
      <ol className="index">
        {shown.map((c, i) => (
          <li key={c.id}>
            <span className="num" aria-hidden>
              {pad(i + 1)}
            </span>
            <span className="main">
              <Link className="primary" to={`/companies/${c.id}`}>
                {c.name}
              </Link>
              <CompanyFacts company={c} />
              <span className="secondary">
                {c.openJobs > 0 ? `${c.openJobs} open job${c.openJobs === 1 ? "" : "s"}` : "No open jobs"} ·{" "}
                {c.lastActivity ? `last activity ${whenLast(c.lastActivity)}` : "nothing logged yet"}
              </span>
            </span>
            <span className="actions">{c.client && <StatusChip status={c.client.status} />}</span>
          </li>
        ))}
        {companies && shown.length === 0 && (
          <li>
            <span className="empty">
              {companies.length === 0 ? "Nothing on your list yet. Search above to add your first company." : "None here."}
            </span>
          </li>
        )}
        {!companies && !load.error && (
          <li>
            <span className="empty">Loading…</span>
          </li>
        )}
      </ol>
    </section>
  );
}
